import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import { Utils } from '../../shared/utils';
import { environment } from '../../../environments/environment';
import { Currencies } from '../../models';
import { OrgaEventStats } from '../components/orga-events/orga-events.component';

@Injectable({
  providedIn: 'root',
})
export class RevenueService {
  private http = inject(HttpClient);

  configUrl = environment.apiUrl;

  getRevenueActivity(dateStart: Date, dateEnd?: Date) {
    let route = `${this.configUrl}o/r/revenue-activity?dateStart=${dateStart.toISOString()}`;
    if (dateEnd) {
      route += `&dateEnd=${dateEnd.toISOString()}`;
    }
    return this.http.get<{
      feesPerCategories: {
        currency: Currencies,
        eventId: number,
        eventName: string,
        fees: number,
        organizerFees: number | '/',
        kycFees: number,
        organizerUpgradeFees: number | '/'
      }[],
      prepaidTickets: {
        currency: Currencies,
        eventId: number,
        eventName: string,
        prepaidTickets: number,
        prepaidAmount: number
      }[],
      waitLists: {
        eventId: number,
        eventName: string,
        waitList: string
      }[],
      soldTicketsNb: Array<{
        eventId: number;
        eventName: string;
        soldTicketsNb: number;
      }>
      }>(route).pipe(
        map( (events) => {
          const compareFeesTickets = Utils.arrayCompare(
            events.feesPerCategories,
            events.prepaidTickets,
            (a, b) => a.eventId === b.eventId,
          );
          const bufferArray =  compareFeesTickets.exist.map((e) => ({
            ...e.new,
            ...e.old,
          })).concat(compareFeesTickets.missing.map( (m) => ({...m, prepaidTickets: 0, prepaidAmount: 0})))
            .concat(compareFeesTickets.removed.map(
              (r) => ({...r, fees: 0, organizerFees: 0, organizerUpgradeFees: 0, kycFees: 0}),
            ));

          const compare = Utils.arrayCompare(bufferArray, events.waitLists, (a, b) => a.eventId === b.eventId);

          const result = compare.exist.map((e) => ({
            ...e.new,
            ...e.old,
          })).concat(compare.missing.map( (m) => ({...m, waitList: '0'})))
            .concat(compare.removed.map((r) => ({
              ...r,
              currency: Currencies.Euro,
              soldTicketsNb: 0,
              fees: 0,
              organizerFees: '/',
              kycFees: 0,
              organizerUpgradeFees: '/',
              prepaidTickets: 0,
              prepaidAmount: 0,
            }))).map((e) => ({
              ...e,
              soldTicketsNb: events?.soldTicketsNb?.find((f)=> f.eventId === e.eventId)?.soldTicketsNb || 0,
            }));
          events?.soldTicketsNb.forEach((eventStat) => {
            const existingEvent = result.find((event) => event.eventId === eventStat.eventId);
            if (existingEvent) {
              existingEvent.soldTicketsNb = eventStat.soldTicketsNb;
            } else {
              result.push({
                ...eventStat,
                currency: Currencies.Euro,
                organizerFees: '/',
                kycFees: 0,
                organizerUpgradeFees: '/',
                eventName: eventStat.eventName,
                waitList: '0',
                prepaidTickets: 0,
                prepaidAmount: 0,
                fees: 0,
              });
            }
          });
          return result;
        }),
      );
  }

  getRevenueTotal(dateStart: Date, dateEnd?: Date) {
    let route = `${this.configUrl}a/r/revenue-total?dateStart=${dateStart.toISOString()}`;
    if (dateEnd) {
      route += `&dateEnd=${dateEnd.toISOString()}`;
    }
    return this.http.get<{
      fees: Array<{fees: number; currency: Currencies; upgradeFees: number}>,
      prepayments: Array<{prepayments: number; currency: Currencies}>
      kycs: Array<{kycs: number; currency: Currencies}>
    }>(route).pipe();
  }

  getGlobalEventsStats(dateStart: Date, dateEnd?: Date) {
    let route = `${this.configUrl}a/r/global-events-stats?dateStart=${dateStart.toISOString()}`;
    if (dateEnd) {
      route += `&dateEnd=${dateEnd.toISOString()}`;
    }
    return this.http.get<{
      capacity: number,
      currency: Currencies,
      eventId: number,
      eventName: string,
      feesAvg: number,
      soldTicketsNb: string,
      waitList: string,
    }[]>(route).pipe(
      map( (events) => events.map( (e) => ({
        ...e,
        soldTicketsNb: parseInt(e.soldTicketsNb, 10),
        waitList: parseInt(e.waitList, 10),
      }))),
    );
  }

  getOrgaStats(dateStart: Date, dateEnd?: Date): Observable<any> {
    let route = `${this.configUrl}a/r/orga-stats?dateStart=${dateStart.toISOString()}`;
    if (dateEnd) {
      route += `&dateEnd=${dateEnd.toISOString()}`;
    }
    return this.http.get<OrgaEventStats[]>(route).pipe(
      map( (statsPerCurrency) => (statsPerCurrency.map( (stat) => ({
        ...stat,
        organizerTotalFees:
          +stat.organizerFeesFlatAmount
          + (+stat.organizerFeesShareAmount / 10000)
          + +stat.organizerUpgradeFees,
      }))
      )),
      catchError((error) => {
        // eslint-disable-next-line no-console
        console.log('Error parsing response', error);
        return throwError(error);
      }),
    );
  }

}
